import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import logo from "../images/logo.png";
import { useAuth } from "../components/AuthContext";

const vetDisclaimerSections = [
  { heading: "Educational Purposes Only", body: "This analysis is provided for general educational and informational purposes only. It is not veterinary medical advice, diagnosis, or treatment, and should not be used as a substitute for professional veterinary care provided by a licensed veterinarian." },
  { heading: "No Veterinary Relationship Formed", body: "No veterinarian-client-patient relationship (VCPR) is established through the use of this tool. A valid VCPR requires in-person examination, sufficient medical history, and an ongoing professional relationship between a licensed veterinarian and the animal being treated." },
  { heading: "Limitations of Visual Analysis", body: "Visual analysis of photographs, X-rays, diagnostic scans, lab reports, prescriptions, and veterinary notes through this tool is inherently limited. Image quality, angle, lighting, resolution, and the absence of clinical context all significantly restrict what can be observed or inferred." },
  { heading: "Do Not Replace Professional Care", body: "Nothing in this analysis should be used to delay, replace, or second-guess veterinary care. If a licensed veterinarian has already provided a diagnosis or treatment plan, do not disregard or alter that plan based on information from this tool." },
  { heading: "Urgency Assessments", body: "Urgency assessments provided here are general guidance only and may not reflect the full clinical picture. When in doubt, always err on the side of seeking veterinary care promptly." },
  { heading: "Medications and Prescriptions", body: "Medication and prescription information provided is for educational context only. Never adjust, discontinue, or administer medications to your pet without direct guidance from a licensed veterinarian." },
  { heading: "Limitation of Liability", body: "Furbabies.AI expressly disclaims all liability for any adverse health outcomes, injury, illness, loss, or harm — to any animal or person — arising from reliance on the information provided. Use of this tool is entirely at your own risk." },
  { heading: "Medical Emergencies", body: "This tool is not intended for use in medical emergencies. If your pet is in immediate danger, contact your nearest emergency veterinary hospital without delay. For suspected poisoning, contact the ASPCA Animal Poison Control Center or the Pet Poison Helpline." },
  { body: "By using Furbabies.AI, you acknowledge that you have read, understood, and agree to the terms of this disclaimer." },
];

const nutritionDisclaimerSections = [
  { heading: "Educational Purposes Only", body: "This analysis is provided for general educational and informational purposes only. It is not intended to constitute, and should not be relied upon as, veterinary medical advice, diagnosis, treatment, or a substitute for professional veterinary care." },
  { heading: "No Veterinary Relationship Formed", body: "No veterinarian-client-patient relationship (VCPR) is established through the use of this tool. A valid VCPR requires a licensed veterinarian to have sufficient knowledge of your animal through a physical, in-person examination and an ongoing relationship — neither of which this tool provides." },
  { heading: "Individual Animal Needs Vary", body: "All nutritional information, meal suggestions, supplement recommendations, and feeding guidance are general in nature. Individual animals have unique health profiles, medical histories, breed-specific needs, and conditions that may make general guidance inappropriate or unsafe. Always consult a licensed veterinarian or board-certified veterinary nutritionist (DACVN) before making changes to your pet's diet." },
  { heading: "Home-Prepared Meals", body: "Home-prepared meals can be a wholesome and healthy choice for your pet when done correctly. However, achieving complete and balanced nutrition at home requires careful planning. Always work with a board-certified veterinary nutritionist to ensure any home-prepared diet fully meets your pet's individual needs." },
  { heading: "Ingredient Safety", body: "Ingredient safety information is provided for general guidance only. Individual animals may have allergies, intolerances, or conditions that make otherwise safe ingredients unsuitable for them. Toxic ingredient warnings reflect currently available knowledge and may not be exhaustive." },
  { heading: "Limitation of Liability", body: "Furbabies.AI expressly disclaims all liability for any adverse health outcomes, injury, illness, loss, or harm to any animal arising from reliance on the information provided. Use of this tool is entirely at your own risk." },
  { heading: "Medical Emergencies", body: "This tool is not intended for use in medical emergencies. If your pet is experiencing a medical emergency, contact your nearest emergency veterinary hospital immediately or call the ASPCA Animal Poison Control Center or the Pet Poison Helpline if poisoning is suspected." },
  { body: "By using Furbabies.AI, you acknowledge that you have read, understood, and agree to the terms of this disclaimer." },
];

const DisclaimerModal = ({ isOpen, onClose, title, sections }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 z-[200]"
        />
        {/* Sheet — slides up from bottom on mobile */}
        <motion.div
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[201] bg-[#F5F0EB] rounded-t-3xl shadow-2xl flex flex-col"
          style={{ maxHeight: "82vh" }}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-[#bda697]" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#d4c4b8] flex-shrink-0">
            <h2 className="text-base font-bold text-gray-800">{title}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-[#e8ddd6] flex items-center justify-center text-[#967d6c] text-xl leading-none">
              ×
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
            {sections.map((section, i) => (
              <div key={i} className="border-l-3 border-l-4 border-[#967d6c] pl-3">
                {section.heading && (
                  <h3 className="font-bold text-gray-800 mb-1 text-xs uppercase tracking-wide">{section.heading}</h3>
                )}
                <p className="text-gray-600 text-sm leading-relaxed">{section.body}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="px-5 py-4 border-t border-[#d4c4b8] flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full py-3 bg-[#967d6c] text-white font-semibold rounded-xl hover:bg-[#bda697] transition-colors text-sm">
              I Understand
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const Navbarsmall = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen]       = useState(false);
  const [isVetDisclaimerOpen, setVetDisclaimerOpen] = useState(false);
  const [isNutDisclaimerOpen, setNutDisclaimerOpen] = useState(false);

  const toggleMobileMenu = () => setMobileMenuOpen(!isMobileMenuOpen);

  const handleLogout = () => { logout(); navigate("/"); };

  const navVariants = {
    hidden:   { opacity: 0, y: -50 },
    visible:  { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <>
      <motion.nav
        variants={navVariants}
        initial="hidden"
        animate="visible"
        className="fixed top-0 left-0 w-full backdrop-blur-md bg-white/70 z-50 flex justify-between items-center p-4"
      >
        <div className="text-lg font-bold text-black-1000 flex items-center">
          <a href="/" target="_blank" rel="noopener noreferrer">
            <img src={logo} alt="Logo" className="h-24" />
          </a>
        </div>

        <div className="xl:hidden flex items-center">
          <button onClick={toggleMobileMenu} className="text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* ── Mobile menu drawer ── */}
        <div className={`${isMobileMenuOpen ? "block" : "hidden"} fixed top-0 right-0 w-full xl:w-1/2 bg-white p-6 h-auto xl:hidden shadow-lg z-50 overflow-y-auto transition-all duration-300`}>
          <div className="flex justify-between items-center mb-8">
            <button onClick={toggleMobileMenu} className="text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Nav links */}
          <ul className="space-y-4 text-lg text-center text-gray-600">
            {[
              { to: "/furhome",      label: "Home" },
              { to: "/pet-health", label: "Pet Health" },
              { to: "/pet-nutrition", label: "Pet Nutrition" },
              { to: "/account",      label: "Account" },
            ].map(({ to, label }) => (
              <motion.li key={to} whileHover={{ scale: 1.1 }} className="hover:text-[#a07855]" onClick={toggleMobileMenu}>
                <Link to={to}>{label}</Link>
              </motion.li>
            ))}
          </ul>

          {/* Disclaimer buttons */}
          {/* <div className="mt-8 space-y-2">
            <p className="text-xs text-center text-gray-400 uppercase tracking-widest mb-3">Disclaimers</p>
            <button
              onClick={() => { toggleMobileMenu(); setVetDisclaimerOpen(true); }}
              className="w-full py-2.5 rounded-xl border border-[#bda697] text-[#967d6c] text-sm font-medium hover:bg-[#f4ede7] transition-colors">
              Pet Health Disclaimer
            </button>
            <button
              onClick={() => { toggleMobileMenu(); setNutDisclaimerOpen(true); }}
              className="w-full py-2.5 rounded-xl border border-[#bda697] text-[#967d6c] text-sm font-medium hover:bg-[#f4ede7] transition-colors">
              Pet Nutrition Disclaimer
            </button>
          </div> */}

          {/* Disclaimer links */}
<ul className="space-y-4 text-lg text-center text-gray-600 mt-6">
  <p className="text-xs text-center text-gray-400 uppercase tracking-widest mb-1">Disclaimers</p>
  {[
    { label: "Pet Health Disclaimer",     action: () => { toggleMobileMenu(); setVetDisclaimerOpen(true); } },
    { label: "Pet Nutrition Disclaimer",  action: () => { toggleMobileMenu(); setNutDisclaimerOpen(true); } },
  ].map(({ label, action }) => (
    <motion.li key={label} whileHover={{ scale: 1.1 }} className="hover:text-[#a07855] list-none" onClick={action}>
      <button className="bg-transparent border-none cursor-pointer text-lg text-gray-500 hover:text-[#a07855]">
        {label}
      </button>
    </motion.li>
  ))}
</ul>

          {/* Logout */}
          <div className="mt-6">
            <motion.button
              onClick={handleLogout}
              whileHover={{ scale: 1.05 }}
              className="bg-[#a07855] text-white px-4 py-2.5 rounded-xl w-full text-sm font-semibold">
              Logout
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* ── Disclaimer modals (bottom sheets) ── */}
      <DisclaimerModal
        isOpen={isVetDisclaimerOpen}
        onClose={() => setVetDisclaimerOpen(false)}
        title="Pet Health Disclaimer"
        sections={vetDisclaimerSections}
      />
      <DisclaimerModal
        isOpen={isNutDisclaimerOpen}
        onClose={() => setNutDisclaimerOpen(false)}
        title="Pet Nutrition Disclaimer"
        sections={nutritionDisclaimerSections}
      />
    </>
  );
};

export default Navbarsmall;